import { useEffect, useRef, useState } from 'react';
import { Link, useMatches, useNavigate } from 'react-router-dom';
import { logout } from '../auth/authService';
import { displayName } from '../auth/sessionStore';
import { useSession } from '../auth/useSession';
import { roleLabel } from './roleLabels';
import { useShellCounters } from './useShellCounters';
import { IconBell, IconSearch } from './icons';
import { IconPlus } from '../v3/drawer';

export interface RouteHandle {
  title?: string;
  subtitle?: string;
  workspace?: string;
  workspaceTo?: string;
  mobileChromeless?: boolean;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iP(hone|ad|od)/.test(navigator.platform ?? '');

/**
 * Тулбар 46px: слева контекст страницы (воркспейс / крошка), справа поиск,
 * напоминания об оплате (единственное уведомление MVP) и меню «•••» с выходом.
 * Инспектор из v2 не используется: карточки — отдельные экраны.
 */
export function GlobalToolbar({ onOpenSearch }: { inspectorOpen?: boolean; onToggleInspector?: () => void; onOpenSearch: () => void }) {
  const matches = useMatches();
  const navigate = useNavigate();
  const session = useSession();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const counters = useShellCounters(Boolean(session.user));

  const handles = matches.map((m) => ({ handle: (m.handle as RouteHandle | undefined) ?? {}, pathname: m.pathname })).filter((m) => m.handle.title);
  const first = handles[0];
  const last = handles[handles.length - 1];
  const ownWorkspace = last?.handle.workspace;
  const isSiblingDetail = Boolean(ownWorkspace) && first === last;
  const workspace = isSiblingDetail ? (ownWorkspace as string) : (first?.handle.workspace ?? first?.handle.title ?? 'WearGroup');
  const crumb = isSiblingDetail ? last?.handle.title : last && last !== first ? last.handle.title : undefined;
  const workspaceTo = isSiblingDetail ? last?.handle.workspaceTo : first?.pathname;

  useEffect(() => {
    if (!overflowOpen) return;
    const onDown = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOverflowOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOverflowOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [overflowOpen]);

  return (
    <header className="toolbar">
      <div className="toolbar__crumbs">
        {crumb && workspaceTo ? <Link className="toolbar__workspace" to={workspaceTo}>{workspace}</Link> : <span className="toolbar__workspace">{workspace}</span>}
        {crumb && (<><span className="toolbar__slash" aria-hidden="true">/</span><span className="toolbar__crumb">{crumb}</span></>)}
        {last?.handle.subtitle && <span className="toolbar__sub">{last.handle.subtitle}</span>}
      </div>
      <div className="toolbar__spacer" />
      <div className="toolbar__utils" ref={menuRef}>
        <button type="button" className="toolbar__new" onClick={() => navigate('/orders/new')} title="Новый заказ"><IconPlus size={11} />Новый заказ</button>
        <button type="button" className="toolbar__search" onClick={onOpenSearch} title="Поиск (Ctrl+K)">
          <IconSearch size={11} />Поиск<kbd>{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
        <button type="button" className="toolbar__util" onClick={() => navigate('/finance?tab=reminders')} title="Напоминания об оплате" aria-label="Напоминания об оплате">
          <IconBell />
          {counters.paymentReminders ? <span className="toolbar__badge" aria-label={`Ждут оплаты: ${counters.paymentReminders}`}>{counters.paymentReminders > 99 ? '99+' : counters.paymentReminders}</span> : null}
        </button>
        <button type="button" className="toolbar__util" aria-haspopup="menu" aria-expanded={overflowOpen} onClick={() => setOverflowOpen((o) => !o)} title="Ещё" aria-label="Ещё">•••</button>
        {overflowOpen && (
          <div className="overflow-menu" role="menu">
            <div className="overflow-menu__user">{displayName(session.user)}<small>{roleLabel(session.user?.roles ?? [])}</small></div>
            <button type="button" className="btn btn--ghost" role="menuitem" onClick={() => { setOverflowOpen(false); navigate('/me'); }}>Личный кабинет</button>
            <button type="button" className="btn btn--ghost" role="menuitem" onClick={() => { setOverflowOpen(false); void logout(); }}>Выйти</button>
          </div>
        )}
      </div>
    </header>
  );
}
